import { useEffect } from "react"
import { Link } from "react-router-dom"

export default function PrivacyPage() {
  useEffect(() => {
    const previousTitle = document.title
    document.title = "Privacy Policy — Apollo"
    return () => {
      document.title = previousTitle
    }
  }, [])

  return (
    <div className="privacy-page">
      <main className="privacy-shell">
        <header className="privacy-header">
          <Link className="privacy-wordmark" to="/" aria-label="Apollo home">
            Apollo
          </Link>
          <h1 className="privacy-title">Privacy Policy</h1>
          <p className="privacy-effective-date">Effective August 1, 2026</p>
          <p className="privacy-intro">
            This Privacy Policy explains how Apollo collects, uses, and shares information when
            you use the Apollo app, our websites, and related services.
          </p>
        </header>

        <article className="privacy-content">
          <section aria-labelledby="information-we-collect">
            <h2 id="information-we-collect">1. Information we collect</h2>
            <p>We may collect the following categories of information:</p>
            <ul>
              <li>
                <strong>Contact and account information,</strong> including your name, phone
                number, email address, verification codes, referral information, and Apollo
                account identifiers.
              </li>
              <li>
                <strong>Linked AMC account information,</strong> including your AMC email,
                password, account identifiers, authentication data, membership information, and
                ticket or reservation details. We encrypt stored AMC credentials and use them to
                access your AMC account only to provide the features you request.
              </li>
              <li>
                <strong>Requests and transaction information,</strong> such as movies, theaters,
                dates, showtimes, seating preferences, booking requests, reservations, service
                fees, and payment status.
              </li>
              <li>
                <strong>Payment information.</strong> Stripe processes payment details for Apollo
                service fees. We receive limited information such as a Stripe customer or payment
                method identifier, card brand, and last four digits, but we do not store full
                payment card numbers.
              </li>
              <li>
                <strong>Device and usage information,</strong> including IP address, app version,
                device and push-notification tokens, activity timestamps, diagnostic logs, crash
                information, and events such as taking a screenshot or starting a screen
                recording while using the app.
              </li>
              <li>
                <strong>Communications,</strong> including messages and support requests you send
                us.
              </li>
            </ul>
            <p>
              Our website also uses local browser storage to remember referral information needed
              for the invite flow. We do not use this information for advertising.
            </p>
          </section>

          <section aria-labelledby="how-we-use-information">
            <h2 id="how-we-use-information">2. How we use information</h2>
            <p>We use information to:</p>
            <ul>
              <li>create and secure your account and verify your phone number;</li>
              <li>connect to AMC and search for, reserve, purchase, and manage tickets;</li>
              <li>process Apollo service fees and maintain transaction records;</li>
              <li>send booking updates, alerts, verification codes, and support messages;</li>
              <li>operate, maintain, troubleshoot, protect, and improve Apollo;</li>
              <li>prevent fraud, abuse, and security incidents; and</li>
              <li>comply with legal obligations and enforce our agreements.</li>
            </ul>
          </section>

          <section aria-labelledby="how-we-share-information">
            <h2 id="how-we-share-information">3. How we share information</h2>
            <p>We may share information with:</p>
            <ul>
              <li>
                <strong>AMC,</strong> when needed to connect your account and carry out the ticket
                searches, reservations, purchases, or other actions you request;
              </li>
              <li>
                <strong>service providers,</strong> such as providers of payment processing, cloud
                hosting, communications, network infrastructure, customer support, and error
                monitoring, who process information for us under appropriate restrictions;
              </li>
              <li>
                <strong>legal and safety recipients,</strong> when we reasonably believe disclosure
                is required by law or necessary to protect Apollo, our users, or others; and
              </li>
              <li>
                <strong>a successor organization,</strong> as part of a merger, financing,
                acquisition, reorganization, bankruptcy, or sale of all or part of our business.
              </li>
            </ul>
            <p>
              We do not sell personal information or share it for cross-context behavioral
              advertising.
            </p>
          </section>

          <section aria-labelledby="retention">
            <h2 id="retention">4. Data retention</h2>
            <p>
              We keep personal information for as long as reasonably necessary to provide Apollo,
              maintain business and transaction records, comply with law, resolve disputes, and
              protect our services. Retention periods vary based on the type of information and
              why we use it. When information is no longer needed, we delete or de-identify it.
            </p>
          </section>

          <section aria-labelledby="choices-and-rights">
            <h2 id="choices-and-rights">5. Your choices and rights</h2>
            <p>
              You can control push notifications in your device settings and may contact us to
              update or correct your information, disconnect your AMC account, or request deletion
              of your Apollo account. Depending on where you live, you may also have rights to
              access, correct, delete, or receive a copy of personal information, or to object to
              or restrict certain processing. We may need to verify your identity before completing
              a request.
            </p>
          </section>

          <section aria-labelledby="security">
            <h2 id="security">6. Security</h2>
            <p>
              We use administrative, technical, and organizational safeguards designed to protect
              personal information. No method of transmission or storage is completely secure, so
              we cannot guarantee absolute security.
            </p>
          </section>

          <section aria-labelledby="children">
            <h2 id="children">7. Children&apos;s privacy</h2>
            <p>
              Apollo is not directed to children under 13, and we do not knowingly collect personal
              information from children under 13. If you believe a child has provided us personal
              information, please contact us so we can take appropriate action.
            </p>
          </section>

          <section aria-labelledby="changes">
            <h2 id="changes">8. Changes to this policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will post the updated version
              here and revise the effective date. If changes are material, we may provide additional
              notice as required by law.
            </p>
          </section>

          <section aria-labelledby="contact-us">
            <h2 id="contact-us">9. Contact us</h2>
            <p>
              Questions or privacy requests can be sent to{" "}
              <a href="mailto:hi@apollo.mov">hi@apollo.mov</a>.
            </p>
          </section>
        </article>
      </main>
    </div>
  )
}
